/**
 * 渠道管理页面
 *
 * @description 渠道合作伙伴管理功能，包括增删改查、二维码生成等
 * @version 1.0.0
 * @author AI Assistant
 * @date 2025-09-24
 *
 * @see docs/design/UI_CONSISTENCY_FRAMEWORK.md
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tooltip,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Image,
  Empty,
  Typography,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  LinkOutlined,
  ReloadOutlined,
  DownloadOutlined,
  GlobalOutlined,
  MobileOutlined,
  WechatOutlined,
  TeamOutlined,
  SettingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { StandardPageLayout } from '../../components/layout';
import {
  UnifiedDataTable,
  UnifiedStatusTag,
  UnifiedFilters,
  useUnifiedConfirm,
} from '../../components/unified';
import type { UnifiedTableColumn } from '../../components/unified/UnifiedDataTable';
import { useRolePermission } from '../../components/auth/RoleBasedRoute';
import {
  useGetChannelsQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useGenerateQRCodeMutation,
  useUpdateChannelStatusMutation,
} from '../../store/api/channelApi';
import type {
  ChannelInfo,
  ChannelTableRow,
  ChannelStatus,
  ChannelType,
} from '../../types/channel';
import { getPublicUrl, getQuestionnaireUrl } from '../../utils/apiConfig';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 渠道类型配置（图标和颜色，label通过t()在组件内获取）
const CHANNEL_TYPE_ICONS: Record<
  string,
  { icon: React.ReactNode; color: string }
> = {
  website: { icon: <GlobalOutlined />, color: '#1890ff' },
  app: { icon: <MobileOutlined />, color: '#52c41a' },
  wechat: { icon: <WechatOutlined />, color: '#13c2c2' },
  partner: { icon: <TeamOutlined />, color: '#722ed1' },
  other: { icon: <SettingOutlined />, color: '#666666' },
};

// 渠道状态颜色配置（label通过t()在组件内获取）
const CHANNEL_STATUS_COLORS: Record<string, string> = {
  active: '#52c41a',
  inactive: '#666666',
  suspended: '#faad14',
};

const ChannelManagement: React.FC = () => {
  const { t } = useTranslation();
  // 权限检查
  const { isExchangeOrAdmin } = useRolePermission();

  // 渠道类型配置（带翻译）
  const CHANNEL_TYPE_CONFIG = useMemo(
    () => ({
      website: {
        ...CHANNEL_TYPE_ICONS.website,
        label: t('channel:type.website'),
      },
      app: { ...CHANNEL_TYPE_ICONS.app, label: t('channel:type.app') },
      wechat: { ...CHANNEL_TYPE_ICONS.wechat, label: t('channel:type.wechat') },
      partner: {
        ...CHANNEL_TYPE_ICONS.partner,
        label: t('channel:type.partner'),
      },
      other: { ...CHANNEL_TYPE_ICONS.other, label: t('channel:type.other') },
    }),
    [t]
  );

  // 渠道状态配置（带翻译）
  const CHANNEL_STATUS_CONFIG = useMemo(
    () => ({
      active: {
        label: t('channel:status.active'),
        color: CHANNEL_STATUS_COLORS.active,
      },
      inactive: {
        label: t('channel:status.inactive'),
        color: CHANNEL_STATUS_COLORS.inactive,
      },
      suspended: {
        label: t('channel:status.suspended'),
        color: CHANNEL_STATUS_COLORS.suspended,
      },
    }),
    [t]
  );
  // 统一确认对话框
  const confirm = useUnifiedConfirm();

  // 状态管理
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQRModalVisible, setIsQRModalVisible] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelInfo | null>(
    null
  );
  const [selectedChannel, setSelectedChannel] = useState<ChannelInfo | null>(
    null
  );
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    status: [] as ChannelStatus[],
    type: [] as ChannelType[],
  });

  // 表单实例
  const [form] = Form.useForm();

  // API查询和变更
  const {
    data: channelsData,
    isLoading,
    refetch,
  } = useGetChannelsQuery(queryParams);
  const [createChannel, { isLoading: isCreating }] = useCreateChannelMutation();
  const [updateChannel, { isLoading: isUpdating }] = useUpdateChannelMutation();
  const [deleteChannel] = useDeleteChannelMutation();
  const [generateQRCode, { isLoading: isGeneratingQR }] =
    useGenerateQRCodeMutation();
  const [updateChannelStatus] = useUpdateChannelStatusMutation();

  // 处理表格数据
  const tableData: ChannelTableRow[] = useMemo(() => {
    if (!channelsData?.data.channels) return [];

    return channelsData.data.channels.map(channel => ({
      ...channel,
      key: channel._id || channel.id || '',
    }));
  }, [channelsData]);

  // 统计数据 - Bug修复: 使用后端返回的全局统计数据，而非当页数据
  const statistics = useMemo(() => {
    // 优先使用后端返回的统计数据（全局统计）
    if (channelsData?.data.statistics) {
      return channelsData.data.statistics;
    }
    // 如果后端没有返回统计，则使用总数据（这是备用方案）
    if (!channelsData?.data.channels)
      return { total: 0, active: 0, inactive: 0, suspended: 0 };

    // 使用后端返回的total作为总数
    const total = channelsData.data.total || channelsData.data.channels.length;
    const channels = channelsData.data.channels;
    return {
      total: total,
      active: channels.filter(c => c.status === 'active').length,
      inactive: channels.filter(c => c.status === 'inactive').length,
      suspended: channels.filter(c => c.status === 'suspended').length,
    };
  }, [channelsData]);

  // 处理创建/编辑渠道
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (editingChannel) {
        // 更新渠道
        await updateChannel({
          _id: editingChannel._id || editingChannel.id || '',
          ...values,
        }).unwrap();
        message.success(t('channel:message.updateSuccess'));
      } else {
        // 创建渠道
        await createChannel(values).unwrap();
        message.success(t('channel:message.createSuccess'));
      }

      setIsModalVisible(false);
      setEditingChannel(null);
      form.resetFields();
      refetch();
    } catch (error: any) {
      message.error(error.message || t('channel:message.operationFailed'));
    }
  }, [editingChannel, form, createChannel, updateChannel, refetch]);

  // 处理编辑
  const handleEdit = useCallback(
    (channel: ChannelTableRow) => {
      setEditingChannel(channel);
      form.setFieldsValue({
        name: channel.name,
        code: channel.code,
        type: channel.type,
        description: channel.description,
        contactPerson: channel.contactPerson,
        contactEmail: channel.contactEmail,
        contactPhone: channel.contactPhone,
        website: channel.website,
        status: channel.status,
      });
      setIsModalVisible(true);
    },
    [form]
  );

  // 处理删除
  const handleDelete = useCallback(
    async (channelId: string) => {
      try {
        await deleteChannel(channelId).unwrap();
        message.success(t('channel:message.deleteSuccess'));
        refetch();
      } catch (error: any) {
        message.error(error.message || t('channel:message.deleteFailed'));
      }
    },
    [deleteChannel, refetch]
  );

  // 处理查看二维码
  const handleViewQRCode = useCallback((channel: ChannelTableRow) => {
    setSelectedChannel(channel);
    setIsQRModalVisible(true);
  }, []);

  // 处理复制链接
  const handleCopyLink = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success(t('channel:qrCode.copySuccess'));
    } catch (error) {
      // 使用备用方法复制
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success(t('channel:qrCode.copySuccess'));
      } catch (fallbackError) {
        message.error(t('channel:message.copyFailed'));
      }
    }
  }, []);

  // 处理下载二维码 - Bug修复: 使用fetch + blob方式下载跨域图片
  const handleDownloadQR = useCallback(
    async (channel: ChannelInfo | null) => {
      if (!channel?.qrCodeUrl) {
        message.error(t('channel:message.qrCodeNotExist'));
        return;
      }

      try {
        message.loading({
          content: t('channel:message.downloadingQR'),
          key: 'download-qr',
        });

        // 使用fetch获取图片blob，解决跨域问题
        const response = await fetch(channel.qrCodeUrl, {
          mode: 'cors',
          credentials: 'omit',
        });

        if (!response.ok) {
          throw new Error(t('channel:message.qrCodeGenerateFailed'));
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // 创建下载链接
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `qrcode_${channel.code || 'channel'}_${Date.now()}.png`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 释放blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

        message.success({
          content: t('channel:qrCode.downloadSuccess'),
          key: 'download-qr',
        });
      } catch (error) {
        console.error('Download QR code failed:', error);
        // 备用方案：使用canvas绘制下载
        try {
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);
            canvas.toBlob(blob => {
              if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `qrcode_${channel.code || 'channel'}_${Date.now()}.png`;
                link.click();
                URL.revokeObjectURL(blobUrl);
                message.success({
                  content: t('channel:qrCode.downloadSuccess'),
                  key: 'download-qr',
                });
              }
            }, 'image/png');
          };
          img.onerror = () => {
            message.error({
              content: t('channel:message.downloadFailed'),
              key: 'download-qr',
            });
          };
          img.src = channel.qrCodeUrl;
        } catch (fallbackError) {
          message.error({
            content: t('channel:message.downloadFailed'),
            key: 'download-qr',
          });
        }
      }
    },
    [t]
  );

  // 处理状态变更
  const handleStatusChange = useCallback(
    async (channelId: string, status: ChannelStatus) => {
      try {
        await updateChannelStatus({ channelId, status }).unwrap();
        message.success(t('channel:message.statusUpdateSuccess'));
        refetch();
      } catch (error: any) {
        message.error(error.message || t('channel:message.statusUpdateFailed'));
      }
    },
    [updateChannelStatus, refetch]
  );

  // 权限检查 - 在所有Hooks之后进行
  if (!isExchangeOrAdmin()) {
    return (
      <StandardPageLayout
        title={t('channel:title')}
        subtitle={t('channel:subtitle')}
        breadcrumbs={[
          { label: t('channel:breadcrumb.home'), path: '/admin' },
          { label: t('channel:breadcrumb.system') },
          { label: t('channel:breadcrumb.channelManagement') },
        ]}
      >
        <Alert
          message={t('channel:message.accessRestricted')}
          description={t('channel:message.noPermission')}
          type="warning"
          showIcon
          style={{ margin: '50px auto', maxWidth: '500px' }}
        />
      </StandardPageLayout>
    );
  }

  // 表格列配置
  const columns: UnifiedTableColumn<ChannelTableRow>[] = [
    {
      title: t('channel:field.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (name: string, record: ChannelTableRow) => (
        <Space>
          {CHANNEL_TYPE_CONFIG[record.type]?.icon}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: t('channel:field.code'),
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {code}
        </Text>
      ),
    },
    {
      title: t('channel:field.type'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: ChannelType) => {
        const config = CHANNEL_TYPE_CONFIG[type];
        return (
          <Tag color={config?.color} style={{ borderRadius: '6px' }}>
            {config?.icon} {config?.label}
          </Tag>
        );
      },
    },
    {
      title: t('channel:field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ChannelStatus) => (
        <UnifiedStatusTag type="channel" status={status} size="small" />
      ),
    },
    {
      title: t('channel:field.contactName'),
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('channel:field.contactEmail'),
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      width: 180,
      ellipsis: true,
      render: (email: string) => (
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {email}
        </Text>
      ),
    },
    {
      title: t('channel:field.questionnaireStats'),
      key: 'statistics',
      width: 120,
      render: (_, record: ChannelTableRow) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: '12px' }}>
            {t('channel:field.statsTotal')}{' '}
            <Text strong>{record.totalQuestionnaires || 0}</Text>
          </Text>
          <Text style={{ fontSize: '12px' }}>
            {t('channel:field.statsPassed')}{' '}
            <Text style={{ color: '#52c41a' }}>
              {record.approvedQuestionnaires || 0}
            </Text>
          </Text>
        </Space>
      ),
    },
    {
      title: t('channel:field.createTime'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {new Date(date).toLocaleDateString()}
        </Text>
      ),
    },
    {
      title: t('channel:field.operation'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record: ChannelTableRow) => (
        <Space size="small">
          <Tooltip title={t('channel:action.viewQRCode')}>
            <Button
              type="text"
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => handleViewQRCode(record)}
              style={{
                color: '#10b981',
                borderColor: '#10b981',
              }}
            />
          </Tooltip>
          <Tooltip title={t('channel:action.copyLink')}>
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleCopyLink(getQuestionnaireUrl(record.code))}
              style={{
                color: '#722ed1',
                borderColor: '#722ed1',
              }}
            />
          </Tooltip>
          <Tooltip title={t('common:action.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                color: '#1890ff',
                borderColor: '#1890ff',
              }}
            />
          </Tooltip>
          <Tooltip title={t('common:action.delete')}>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => {
                confirm.show({
                  title: t('channel:modal.deleteTitle'),
                  content: (
                    <div>
                      <p>
                        {t('channel:message.deleteConfirm', {
                          name: record.name,
                        })}
                      </p>
                      <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                        ⚠️ {t('channel:message.dataUnrecoverable')}
                      </p>
                    </div>
                  ),
                  onConfirm: () => handleDelete(record._id || record.id || ''),
                  okText: t('common:action.confirm'),
                  type: 'error',
                });
              }}
              style={{
                color: '#ff4d4f',
                borderColor: '#ff4d4f',
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 页面配置
  const pageConfig = {
    title: t('channel:title'),
    description: t('channel:subtitle'),
  };

  // 面包屑导航
  const breadcrumbs = [
    { label: t('channel:breadcrumb.home'), path: '/admin' },
    { label: t('channel:breadcrumb.system') },
    { label: t('channel:breadcrumb.channelManagement') },
  ];

  // 页面操作按钮
  const actionButtons = [
    {
      key: 'refresh',
      label: t('channel:action.refresh'),
      icon: <ReloadOutlined />,
      onClick: () => refetch(),
    },
    {
      key: 'create',
      label: t('channel:action.create'),
      type: 'primary' as const,
      icon: <PlusOutlined />,
      onClick: () => {
        setEditingChannel(null);
        form.resetFields();
        setIsModalVisible(true);
      },
    },
  ];

  return (
    <StandardPageLayout
      title={pageConfig.title}
      subtitle={t('channel:subtitleDesc')}
      breadcrumbs={breadcrumbs}
      statistics={[
        {
          value: statistics.total,
          label: t('channel:stats.total'),
          color: '#262626',
        },
        {
          value: statistics.active,
          label: t('channel:stats.active'),
          color: '#10b981',
        },
        {
          value: statistics.suspended,
          label: t('channel:stats.suspended'),
          color: '#52c41a',
        },
        {
          value: statistics.inactive,
          label: t('channel:stats.inactive'),
          color: '#1890ff',
        },
      ]}
      actions={actionButtons}
      icon={<TeamOutlined style={{ fontSize: '24px', color: 'white' }} />}
      tags={[
        t('channel:tags.channelManagement'),
        t('channel:tags.dataAnalysis'),
        t('channel:tags.qrTracking'),
      ]}
    >
      {/* 筛选区域 */}
      <UnifiedFilters
        filters={queryParams}
        filterConfigs={[
          {
            key: 'search',
            type: 'search',
            placeholder: t('channel:filter.searchDetailPlaceholder'),
            span: 8,
          },
          {
            key: 'status',
            type: 'multiSelect',
            placeholder: t('channel:filter.statusPlaceholder'),
            span: 6,
            options: Object.entries(CHANNEL_STATUS_CONFIG).map(
              ([value, config]) => ({
                label: config.label,
                value,
              })
            ),
          },
          {
            key: 'type',
            type: 'multiSelect',
            placeholder: t('channel:filter.typePlaceholder'),
            span: 6,
            options: Object.entries(CHANNEL_TYPE_CONFIG).map(
              ([value, config]) => ({
                label: config.label,
                value,
              })
            ),
          },
        ]}
        onFiltersChange={(key, value) => {
          if (key === 'search') {
            setQueryParams(prev => ({
              ...prev,
              search: value,
              page: 1,
            }));
          } else {
            setQueryParams(prev => ({ ...prev, [key]: value, page: 1 }));
          }
        }}
        onClearFilters={() => {
          setQueryParams({
            page: 1,
            limit: 10,
            search: '',
            status: [],
            type: [],
          });
        }}
      />

      {/* 数据表格 */}
      <UnifiedDataTable<ChannelTableRow>
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('common:message.noData')}
            />
          ),
        }}
        pagination={{
          current: queryParams.page,
          pageSize: queryParams.limit,
          total: channelsData?.data.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:pagination.range', {
              start: range[0],
              end: range[1],
              total,
            }),
          onChange: (page, pageSize) => {
            setQueryParams(prev => ({ ...prev, page, limit: pageSize || 10 }));
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(selectedRowKeys.map(key => String(key)));
          },
        }}
        scroll={{ x: 1200 }}
      />

      {/* 创建/编辑渠道弹框 */}
      <Modal
        title={
          <div
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 24px',
              margin: '-16px -24px 16px -24px',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Space>
              <TeamOutlined style={{ fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                {editingChannel
                  ? t('channel:modal.editTitle')
                  : t('channel:modal.createTitle')}
              </span>
            </Space>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingChannel(null);
          form.resetFields();
        }}
        footer={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              padding: '16px 24px',
              margin: '16px -24px -16px -24px',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingChannel(null);
                  form.resetFields();
                }}
                style={{
                  borderRadius: '6px',
                  height: '36px',
                  fontWeight: 500,
                  minWidth: '80px',
                  borderColor: '#10b981',
                  color: '#10b981',
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={isCreating || isUpdating}
                style={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '6px',
                  height: '36px',
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  minWidth: '80px',
                }}
              >
                {editingChannel
                  ? t('common:action.update')
                  : t('common:action.create')}
              </Button>
            </div>
          </div>
        }
        width={720}
        destroyOnClose
        styles={{
          body: {
            padding: '24px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.name')}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t('channel:validation.nameRequired'),
                  },
                ]}
              >
                <Input
                  placeholder={t('channel:field.namePlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#059669';
                    e.target.style.boxShadow =
                      '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('channel:field.code')} name="code">
                <Input
                  placeholder={t('channel:field.codeAutoGenerate')}
                  disabled
                  style={{
                    backgroundColor: '#f5f5f5',
                    color: '#999',
                    cursor: 'not-allowed',
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.type')}
                name="type"
                rules={[
                  {
                    required: true,
                    message: t('channel:validation.typeRequired'),
                  },
                ]}
              >
                <Select
                  placeholder={t('channel:field.typePlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  dropdownStyle={{
                    borderColor: '#10b981',
                  }}
                >
                  {Object.entries(CHANNEL_TYPE_CONFIG).map(
                    ([value, config]) => (
                      <Select.Option key={value} value={value}>
                        {config.icon} {config.label}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.status')}
                name="status"
                rules={[
                  {
                    required: true,
                    message: t('channel:field.statusRequired'),
                  },
                ]}
              >
                <Select
                  placeholder={t('channel:field.statusPlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  dropdownStyle={{
                    borderColor: '#10b981',
                  }}
                >
                  {Object.entries(CHANNEL_STATUS_CONFIG).map(
                    ([value, config]) => (
                      <Select.Option key={value} value={value}>
                        {config.label}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('channel:field.description')} name="description">
            <TextArea
              placeholder={t('channel:field.descriptionPlaceholder')}
              rows={3}
              showCount
              maxLength={200}
              style={{
                borderColor: '#10b981',
                borderRadius: '6px',
              }}
            />
          </Form.Item>

          {/* 显示平台网站地址 */}
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#374151',
                marginBottom: '4px',
              }}
            >
              {t('channel:qrCode.linkPrefix')}
            </div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#10b981',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              }}
            >
              {getPublicUrl()}/questionnaire?channel=
            </div>
            <div
              style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}
            >
              {t('channel:qrCode.linkSuffix')}
            </div>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.contactName')}
                name="contactPerson"
                rules={[
                  {
                    required: true,
                    message: t('channel:field.contactNamePlaceholder'),
                  },
                ]}
              >
                <Input
                  placeholder={t('channel:field.contactNamePlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#059669';
                    e.target.style.boxShadow =
                      '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.contactEmail')}
                name="contactEmail"
                rules={[
                  {
                    required: true,
                    message: t('channel:field.contactEmailPlaceholder'),
                  },
                  {
                    type: 'email',
                    message: t('channel:field.contactEmailInvalid'),
                  },
                ]}
              >
                <Input
                  placeholder={t('channel:field.contactEmailPlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#059669';
                    e.target.style.boxShadow =
                      '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.contactPhone')}
                name="contactPhone"
              >
                <Input
                  placeholder={t('channel:field.contactPhonePlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#059669';
                    e.target.style.boxShadow =
                      '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('channel:field.website')}
                name="website"
                rules={[
                  { type: 'url', message: t('channel:field.websiteInvalid') },
                ]}
              >
                <Input
                  placeholder={t('channel:field.websitePlaceholder')}
                  style={{
                    borderColor: '#10b981',
                    borderRadius: '6px',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#059669';
                    e.target.style.boxShadow =
                      '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 二维码查看弹框 */}
      <Modal
        title={t('channel:qrCode.title')}
        open={isQRModalVisible}
        onCancel={() => {
          setIsQRModalVisible(false);
          setSelectedChannel(null);
        }}
        footer={[
          <Button
            key="copy"
            onClick={() =>
              selectedChannel &&
              handleCopyLink(getQuestionnaireUrl(selectedChannel.code))
            }
          >
            <LinkOutlined /> {t('channel:action.copyLink')}
          </Button>,
          <Button
            key="download"
            type="primary"
            disabled={!selectedChannel?.qrCodeUrl}
            onClick={() => handleDownloadQR(selectedChannel)}
          >
            <DownloadOutlined /> {t('channel:action.downloadQRCode')}
          </Button>,
        ]}
        width={400}
      >
        {selectedChannel && (
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>{selectedChannel.name}</Title>
            <Paragraph type="secondary">
              {t('channel:qrCode.scanDescription')}
            </Paragraph>

            {selectedChannel.qrCodeUrl ? (
              <Image
                src={selectedChannel.qrCodeUrl}
                alt={t('channel:qrCode.alt')}
                width={200}
                height={200}
                style={{ border: '1px solid #d9d9d9', borderRadius: '8px' }}
              />
            ) : (
              <div
                style={{
                  width: 200,
                  height: 200,
                  border: '1px dashed #d9d9d9',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                }}
              >
                <Button
                  type="link"
                  icon={<QrcodeOutlined />}
                  onClick={() =>
                    selectedChannel &&
                    generateQRCode({
                      channelId:
                        selectedChannel._id || selectedChannel.id || '',
                    })
                  }
                  loading={isGeneratingQR}
                >
                  {t('channel:action.generateQRCode')}
                </Button>
              </div>
            )}

            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  flex: 1,
                }}
              >
                {getQuestionnaireUrl(selectedChannel.code)}
              </Text>
              <Tooltip title={t('channel:action.copyLink')}>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() =>
                    handleCopyLink(getQuestionnaireUrl(selectedChannel.code))
                  }
                  style={{
                    color: '#10b981',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </div>
          </div>
        )}
      </Modal>
    </StandardPageLayout>
  );
};

export default ChannelManagement;
